import { useEffect } from 'react'
import { useFormContext } from 'react-hook-form'
import { useDispatch } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { importStatus } from '@components/buttons/importList/types'
import { UploaderAnimation } from '@components/buttons/importList/uploadSvg'
import { useTaxonomyTranslation } from '@global/hooks/queries/taxonomy-translations'
import * as SnackbarActions from '@global/snackbar/snackbar.actions'
import { TaxonomyTranslationStatus } from '@taxonomy-translation/types'
import { ROUTES } from 'routes'
import { LABELS_BY_DATA_TYPE } from '../constants'
import { useTranslationUploadStore } from '../hooks'
import { DataType, FormFile, TemplateItem, TranslationUploadForm } from '../types'
import { FileCard } from './file-card'
import { getFileSizeText } from '../utils'
import { useStepperStore } from './stepper'
import { StepsInfo } from './steps-info'
import { t } from '@lingui/core/macro'

export const StepThree = () => {
  const history = useHistory()
  const dispatch = useDispatch()

  const { watch } = useFormContext<TranslationUploadForm>()
  const { setIsCompleted, resetStepper } = useStepperStore()
  const { id, isLoading, error, setIsLoading, setError, resetTranslationUpload } =
    useTranslationUploadStore()

  const {
    data: translation,
    isError,
    error: queryError,
  } = useTaxonomyTranslation(id!, {
    enabled: !!id,
    refetchInterval: 10000, // 10s
  })

  const { stepOne, stepTwo } = watch()

  const renderFiles = (dataType: DataType, files: FormFile[]) =>
    dataType === 'files' && files.length
      ? files.map((file, index) => (
          <FileCard
            key={file.name + String(index)}
            title={file.name}
            subtitle={getFileSizeText(file.size)}
            objective={file.objective}
            className="mb-4"
            isDisabled
          />
        ))
      : null

  const renderTemplates = (dataType: DataType, templates: TemplateItem[]) =>
    dataType === 'templates' && templates.length
      ? templates.map(template => (
          <FileCard
            key={template.id}
            title={template.title}
            subtitle={template.subtitle}
            className="mb-4"
          />
        ))
      : null

  const getTitle = () => {
    // TODO: add translation.status cases
    if (isLoading) return 'Translating your data...'

    return 'Review'
  }

  useEffect(() => {
    // querry failed
    if (isLoading && isError) {
      setIsLoading(false)
      setIsCompleted(false)
      setError(queryError)
      //  TODO: when error mention any field add this error to related field in step one or two

      return
    }

    // querry with status completed
    if (
      typeof translation?.status === 'string' &&
      translation.status !== TaxonomyTranslationStatus.InProgress &&
      isLoading
    ) {
      if (translation.status === TaxonomyTranslationStatus.Completed) {
        history.replace(ROUTES.TAXONOMY_TRANSLATION_DETAILS.replace(':id', id ?? ''))
        dispatch(
          SnackbarActions.showSnackbar({
            message: 'Translation successful',
            type: 'SUCCESS',
            duration: 3000,
          }),
        )
      }
      if (translation.status === TaxonomyTranslationStatus.Failed) {
        history.replace(ROUTES.TAXONOMY_TRANSLATION)

        SnackbarActions.showSnackbar({
          message: 'Translation failed - unknown error',
          type: 'ERROR',
          duration: 6000,
        })
      }

      resetTranslationUpload()
      resetStepper()
    }
  }, [translation?.status, isLoading, isError, setIsLoading, resetTranslationUpload])

  return (
    <div className="bg-white h-[calc(100vh-100px)] overflow-y-auto">
      <div className="flex flex-col max-w-[604px] mx-auto h-full">
        <h3 className="font-bold text-[21px] text-slate-800 mt-[56px] text-center">{getTitle()}</h3>

        {error ? (
          <div className="mt-5 mb-6 p-6 bg-red-50 rounded-sm">
            <p className="text-slate-800 font-semibold">{t`Translation failed`}</p>
            <p className="text-slate-800 mt-4">
              {error instanceof Error ? error.message : 'Something went wrong'}
            </p>
          </div>
        ) : null}

        {!isLoading ? (
          <>
            <p className="mt-2 mb-3 text-slate-800">
              <span className="font-bold">{t`Data to be translated`}</span> -{' '}
              {LABELS_BY_DATA_TYPE[stepOne.dataType] || null}
            </p>

            {renderFiles(stepOne.dataType, stepOne.files)}
            {renderTemplates(stepOne.dataType, stepOne.templates)}

            <p className="mt-5 mb-3 text-slate-800">
              <span className="font-bold">{t`Baseline data`}</span> -{' '}
              {LABELS_BY_DATA_TYPE[stepTwo.dataType] || null}
            </p>

            {renderFiles(stepTwo.dataType, stepTwo.files)}
            {renderTemplates(stepTwo.dataType, stepTwo.templates)}

            <div className="grow" />

            <StepsInfo />
          </>
        ) : (
          <>
            <div className="self-center mt-[56px] [&>svg]:w-[402px] [&>svg]:h-[218px]">
              <UploaderAnimation
                status={importStatus.DEFAULT} // TODO: add translation.status cases
                uploadInProgress={true}
                uploadComplete={false}
              >
                <UploaderAnimation.ProgressCircle status={importStatus.DEFAULT} />
              </UploaderAnimation>
            </div>
            <p className="text-slate-800 text-center mt-[84px]">
              {t`Translation could take up to 5 minutes - feel free to leave this page and come back.`}
            </p>
          </>
        )}
      </div>
    </div>
  )
}
